import { useRsrc } from '@/common/rsrc/RsrcContext';
import { useEffect, useState } from 'react';
import { SankeyChart } from '@/common/dashboard/charts/SankeyChart';

export const FinanceDashboardSankeyChart = ({
  startPeriod,
  endPeriod,
  periodicity,
  prodIds,
  channelIds,
  entity,
  hoveredCategory
}) => {
  const rsrc = useRsrc();
  const [data, setData] = useState();
  const selectedPeriod = hoveredCategory;

  useEffect(() => {
    if (!prodIds?.length || !channelIds?.length) return;

    const ndc11ProdIds = prodIds.map((prod) => prod);
    const reqParam = {
      selectedPeriod,
      startPeriod,
      endPeriod,
      periodicity,
      ndc11ProdIds,
      channelIds,
      entity,
    };

    rsrc
      .data('gtn', 'PL_WATERFALL_DASHBOARD_DATA_PLUGIN', reqParam, entity)
      .then((rows) => setData(rows));
  }, [selectedPeriod, startPeriod, endPeriod, periodicity, prodIds, channelIds, entity]);

  if (!data) return null;
  if (!data.series.length) return null;

  const input = data.series;

  // --- CONFIG ---
  const rebateLabels = ['Medicaid', 'Industrial Funding Fee', 'Managed Care (YD)'];

  // --- STEP 1: Separate sources and deductions ---
  const sources = input.filter(d =>
    ['US Gross Sales'].includes(d.label)
  );

  const deductions = input.filter(
    d => !['US Gross Sales', 'Uncategorized Direct Sales', 'Net Sales'].includes(d.label)
  );

  // --- STEP 2: Totals ---
  const totalInflow = sources.reduce((sum, d) => sum + d.value, 0);
  const totalDeductions = deductions.reduce(
    (sum, d) => (d.value < 0 ? sum + d.value : sum),
    0
  );
  const netSalesValue = totalInflow + totalDeductions;

  // --- STEP 3: Build links ---
  const links = [];

  // Define color palette
  const COLORS = {
    red: '#E53935',     // Flows INTO Rebate or Chargeback buckets
    orange: '#FB8C00',  // Rebate children
    yellow: '#FDD835',  // Chargeback children
    green: '#43A047',   // Flows to Net Sales
  };

  // 3a. Gross Sales → Middle categories
  for (const src of sources) {
    // Rebates flow
    links.push({
      from: src.label,
      to: 'Rebates',
      weight: Math.abs(
        deductions
          .filter(d => rebateLabels.includes(d.label))
          .reduce((s, d) => s + d.value, 0)
      ),
      color: COLORS.red,
    });

    // Chargebacks flow
    links.push({
      from: src.label,
      to: 'Chargebacks',
      weight: Math.abs(
        deductions
          .filter(d => !rebateLabels.includes(d.label))
          .reduce((s, d) => s + d.value, 0)
      ),
      color: COLORS.red,
    });
  }

  // 🔹 Sort grandchildren (deductions) by absolute value, descending
  const sortedDeductions = [...deductions].sort(
    (a, b) => Math.abs(b.value) - Math.abs(a.value)
  );

  // 3b. Middle categories → Individual deductions (now sorted largest → smallest)
  for (const d of sortedDeductions) {
    const absVal = Math.abs(d.value);
    if (rebateLabels.includes(d.label)) {
      links.push({
        from: 'Rebates',
        to: d.label,
        weight: absVal,
        color: COLORS.orange,
      });
    } else if (d.value < 0) {
      links.push({
        from: 'Chargebacks',
        to: d.label,
        weight: absVal,
        color: COLORS.yellow,
      });
    }
  }

  // 3c. Sources → Net Sales (the remainder)
  for (const src of sources) {
    const srcShare = src.value / totalInflow;
    links.push({
      from: src.label,
      to: 'Net Sales',
      weight: netSalesValue * srcShare,
      color: COLORS.green,
    });
  }

  return <SankeyChart dataLabels data={links} />;
};
